import React, { useEffect, useState } from 'react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Building2, MapPin, Users, DollarSign, Plus, Edit, House, Search, Filter, Wrench, FileText, CreditCard, Users2, Clock, Zap, Shield, Landmark } from 'lucide-react';
import { LoadingScreen } from '@/components/common/LoadingSpinner';
import { HubActionCard } from '@/components/cards/HubActionCard';

export const PropertyDetails: React.FC = () => {
    const { propertyId } = useParams();
    const navigate = useNavigate();
    const {
        currentProperty,
        units,
        tenants,
        invoices,
        loading,
        fetchPropertyDetails,
        fetchUnits,
        fetchTenants,
        fetchInvoices
    } = useLandlordStore();

    const [activeTab, setActiveTab] = useState<'overview' | 'units' | 'tenants' | 'financials'>('overview');

    // Support deep linking / navigation from property hub cards via query param.
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab === 'units' || tab === 'tenants' || tab === 'financials' || tab === 'overview') {
            setActiveTab(tab);
        }
    }, [searchParams]);
    useEffect(() => {
        if (propertyId) {
            fetchPropertyDetails(propertyId);
            fetchUnits(propertyId);
            if (tenants.length === 0) fetchTenants();
            if (invoices.length === 0) fetchInvoices();
        }
    }, [propertyId]);

    // Data filtering for the selected property
    const propertyTenants = tenants.filter((t: any) => t.propertyId === propertyId);

    const propertyInvoices = invoices.filter(inv => {
        // Infer property ID for this invoice using the tenants list
        const matchingTenant = tenants.find((t: any) =>
            t.name === inv.tenant_name && t.unitName === inv.unit_number
        ) as any;
        return matchingTenant?.propertyId === propertyId;
    });

    if (loading && !currentProperty) return <LoadingScreen />;

    if (!currentProperty) {
        return <div className="p-8 text-center">Property not found</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4 mb-2">
                <button
                    onClick={() => navigate('/landlord/properties')}
                    className="p-2 rounded-full hover:bg-gray-100 dark:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-100 dark:bg-gray-800 transition-colors"
                >
                    <ArrowLeft size={24} className="text-light-subtle dark:text-dark-subtle dark:text-gray-300" />
                </button>
                <h1 className="text-2xl font-bold text-light-text dark:text-dark-text dark:text-white">Property Details</h1>
            </div>

            {/* Property Overview Card */}
            <div className="bg-light-card dark:bg-dark-card dark:bg-dark-card rounded-lg shadow-sm border border-light-border dark:border-dark-border overflow-hidden">
                <div className="relative h-64 bg-gray-100 dark:bg-gray-800">
                    {currentProperty.images?.[0] ? (
                        <img src={currentProperty.images[0]} alt={currentProperty.name} className="w-full h-full object-cover" />
                    ) : (
                        /* Light mode: vibrant primary→golden hero gradient */
                        /* Dark mode: dark card with subtle golden accent (existing style) */
                        <div className="w-full h-full
                            bg-gradient-to-br from-primary via-primary/90 to-golden/60
                            dark:bg-gradient-to-br dark:from-dark-card dark:via-dark-card dark:to-golden/20
                            flex items-center justify-center relative overflow-hidden">
                            {/* decorative blobs — light mode only */}
                            <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-golden/20 blur-2xl dark:hidden" />
                            <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-white/10 blur-xl dark:hidden" />
                            {/* icon */}
                            <Building2 size={64} className="text-white/40 dark:text-golden/30 relative z-10" />
                        </div>
                    )}
                    <div className="absolute top-4 right-4 flex gap-2">
                        <button
                            onClick={() => navigate(`/landlord/properties/edit/${currentProperty.id}`)}
                            className="bg-white/20 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-white/30 transition-colors flex items-center gap-2 border border-white/20"
                        >
                            <Edit size={16} /> Edit
                        </button>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 text-white">
                        <h2 className="text-3xl font-bold mb-2">{currentProperty.name}</h2>
                        <div className="flex items-center gap-2 text-gray-200">
                            <MapPin size={18} />
                            <span>{currentProperty.address}</span>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-light-border dark:divide-dark-border dark:divide-light-border dark:divide-dark-border">
                    <div className="p-6">
                        <p className="text-sm text-light-subtle dark:text-dark-subtle mb-1">Category</p>
                        <p className="text-lg font-semibold text-light-text dark:text-dark-text dark:text-white">{currentProperty.category}</p>
                    </div>
                    <div className="p-6">
                        <p className="text-sm text-light-subtle dark:text-dark-subtle mb-1">Total Units</p>
                        <p className="text-lg font-semibold text-light-text dark:text-dark-text dark:text-white">{units.length}</p>
                    </div>
                    <div className="p-6">
                        <p className="text-sm text-light-subtle dark:text-dark-subtle mb-1">Occupancy</p>
                        <div className="flex items-center gap-2">
                            <Users size={20} className="text-primary" />
                            <p className="text-lg font-semibold text-light-text dark:text-dark-text dark:text-white">
                                {units.filter(u => u.status === 'Occupied').length} / {units.length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Property Hub - action sections like mobile */}
            <div className="space-y-8">
                <div>
                    <h2 className="text-xl font-semibold text-light-text dark:text-dark-text">Property Management</h2>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle mt-1">Quick access to the tools you need to run this property.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                        <HubActionCard
                            title="Manage Units"
                            description="View and update units for this property."
                            icon={House}
                            onClick={() => navigate(`/landlord/units/${currentProperty.id}/${encodeURIComponent(currentProperty.name)}`)}
                            accentColor="bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400"
                        />
                        <HubActionCard
                            title="Manage Tenants"
                            description="View and manage tenant records."
                            icon={Users2}
                            onClick={() => navigate(`/landlord/tenants?propertyId=${currentProperty.id}`)}
                            accentColor="bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
                        />
                        <HubActionCard
                            title="Viewing Requests"
                            description="Review upcoming viewing requests."
                            icon={Clock}
                            onClick={() => navigate(`/landlord/bookings?propertyId=${currentProperty.id}`)}
                            accentColor="bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400"
                        />
                        <HubActionCard
                            title="Maintenance"
                            description="Track and manage repair requests."
                            icon={Wrench}
                            onClick={() => navigate(`/landlord/maintenance?propertyId=${currentProperty.id}`)}
                            accentColor="bg-yellow-50 text-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400"
                        />
                    </div>
                </div>

                <div>
                    <h2 className="text-xl font-semibold text-light-text dark:text-dark-text">Marketing & Listings</h2>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle mt-1">Tools to advertise and update this property's listings.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                        <HubActionCard
                            title="Edit Property"
                            description="Update property details and photos."
                            icon={Edit}
                            onClick={() => navigate(`/landlord/properties/edit/${currentProperty.id}`)}
                            accentColor="bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400"
                        />
                        <HubActionCard
                            title="Post Vacancy"
                            description="Advertise available units on the platform."
                            icon={FileText}
                            onClick={() => navigate(`/landlord/vacancies?propertyId=${currentProperty.id}`)}
                            accentColor="bg-pink-50 text-pink-600 dark:bg-pink-900/20 dark:text-pink-400"
                        />
                        <HubActionCard
                            title="Register Unit"
                            description="Add a new unit to this property."
                            icon={Plus}
                            onClick={() => navigate(`/landlord/units/register?propertyId=${currentProperty.id}&propertyName=${encodeURIComponent(currentProperty.name)}`)}
                            accentColor="bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400"
                        />
                    </div>
                </div>

                <div>
                    <h2 className="text-xl font-semibold text-light-text dark:text-dark-text">Financials</h2>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle mt-1">Review revenue, invoices, and billing tools.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                        <HubActionCard
                            title="Financials"
                            description="View invoices and revenue for this property."
                            icon={CreditCard}
                            onClick={() => navigate(`/landlord/financials?propertyId=${currentProperty.id}`)}
                            accentColor="bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400"
                        />
                        <HubActionCard
                            title="Manage Utilities"
                            description="Set utility fees and billing rules."
                            icon={Zap}
                            onClick={() => navigate(`/landlord/utilities?propertyId=${currentProperty.id}`)}
                            accentColor="bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400"
                        />
                        <HubActionCard
                            title="Reports"
                            description="View property performance and insights."
                            icon={Building2}
                            onClick={() => navigate(`/landlord/reports?propertyId=${currentProperty.id}`)}
                            accentColor="bg-sky-50 text-sky-600 dark:bg-sky-900/20 dark:text-sky-400"
                        />
                        <HubActionCard
                            title="Payout Methods"
                            description="Manage bank and Tuma M-Pesa connections."
                            icon={Landmark}
                            onClick={() => navigate(`/landlord/payment-methods`)}
                            accentColor="bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400"
                        />
                    </div>
                </div>

                <div>
                    <h2 className="text-xl font-semibold text-light-text dark:text-dark-text">Team & Premium</h2>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle mt-1">Manage staff and premium features.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                        <HubActionCard
                            title="Manage Staff"
                            description="Manage property personnel and roles."
                            icon={Shield}
                            onClick={() => navigate(`/landlord/staff?propertyId=${currentProperty.id}`)}
                            accentColor="bg-sky-50 text-sky-600 dark:bg-sky-900/20 dark:text-sky-400"
                        />
                    </div>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="border-b border-light-border dark:border-dark-border flex gap-6">
                {['overview', 'units', 'tenants', 'financials'].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab as any)}
                        className={`pb-3 text-sm font-medium capitalize transition-colors border-b-2 ${activeTab === tab
                            ? 'border-primary text-primary dark:border-golden dark:text-golden'
                            : 'border-transparent text-light-subtle dark:text-dark-subtle hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* Content Sections */}
            <div className="min-h-[300px]">
                {activeTab === 'overview' && (
                    <div className="space-y-6">
                        <div className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-6 rounded-lg shadow-sm border border-light-border dark:border-dark-border">
                            <h3 className="text-lg font-bold text-light-text dark:text-dark-text dark:text-white mb-4">Description</h3>
                            <p className="text-light-subtle dark:text-dark-subtle dark:text-gray-300">
                                {currentProperty.description || 'No description provided.'}
                            </p>
                        </div>
                    </div>
                )}

                {activeTab === 'units' && (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <div className="flex gap-2">
                                <div className="relative">
                                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" />
                                    <input
                                        type="text"
                                        placeholder="Search units..."
                                        className="pl-9 pr-4 py-2 text-sm border border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border rounded-lg bg-light-bg dark:bg-dark-bg dark:bg-gray-100 dark:bg-gray-800 outline-none focus:border-primary dark:focus:border-golden"
                                    />
                                </div>
                                <button className="p-2 border border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border rounded-lg bg-light-card dark:bg-dark-card dark:bg-dark-card hover:bg-light-bg dark:bg-dark-bg dark:hover:bg-gray-100 dark:bg-gray-800">
                                    <Filter size={18} className="text-light-subtle dark:text-dark-subtle" />
                                </button>
                            </div>
                            <button
                                onClick={() => navigate(`/landlord/units/register?propertyId=${currentProperty.id}&propertyName=${encodeURIComponent(currentProperty.name)}`)}
                                className="flex items-center gap-2 bg-primary dark:bg-golden text-white px-4 py-2 rounded-lg hover:opacity-90 transition-colors"
                            >
                                <Plus size={20} />
                                Add Unit
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {units.map((unit) => (
                                <div
                                    key={unit.id}
                                    onClick={() => navigate(`/landlord/units/detail/${currentProperty.id}/${unit.id}`)}
                                    className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-4 rounded-lg shadow-sm border border-light-border dark:border-dark-border hover:border-primary transition-colors cursor-pointer group"
                                >
                                    <div className="flex justify-between items-start mb-3">
                                        <h3 className="text-lg font-bold text-light-text dark:text-dark-text dark:text-white">Unit {unit.unit_number}</h3>
                                        <span className={`px-2 py-1 text-xs rounded-full font-medium
                                            ${unit.status === 'Available' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                                unit.status === 'Occupied' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                                    'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                            }`}
                                        >
                                            {unit.status}
                                        </span>
                                    </div>

                                    <div className="space-y-2 mb-4">
                                        <div className="flex items-center text-sm text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle">
                                            <House size={16} className="mr-2" />
                                            <span>{unit.bedrooms} Bed, {unit.bathrooms} Bath</span>
                                        </div>
                                        <div className="flex items-center text-sm text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle">
                                            <DollarSign size={16} className="mr-2" />
                                            <span>KES {unit.rent_amount.toLocaleString()} / mo</span>
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-2 text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button className="text-primary hover:underline">View Details</button>
                                    </div>
                                </div>
                            ))}
                            {units.length === 0 && (
                                <div className="col-span-1 md:col-span-2 lg:col-span-3 py-12 text-center text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle bg-light-card dark:bg-dark-card dark:bg-dark-card rounded-lg border border-dashed border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border">
                                    <p>No units added to this property yet.</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'tenants' && (
                    <div className="bg-light-card dark:bg-dark-card dark:bg-dark-card rounded-lg shadow-sm border border-light-border dark:border-dark-border overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-light-bg dark:bg-dark-bg dark:bg-gray-900/50">
                                    <tr>
                                        <th className="px-6 py-4 text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Tenant</th>
                                        <th className="px-6 py-4 text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Unit</th>
                                        <th className="px-6 py-4 text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Contact</th>
                                        <th className="px-6 py-4 text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-light-border dark:divide-dark-border dark:divide-light-border dark:divide-dark-border">
                                    {propertyTenants.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="px-6 py-8 text-center text-light-subtle dark:text-dark-subtle">
                                                No tenants found for this property.
                                            </td>
                                        </tr>
                                    ) : (
                                        propertyTenants.map((t: any) => (
                                            <tr key={t.id} className="hover:bg-light-bg dark:bg-dark-bg dark:hover:bg-gray-900/50">
                                                <td className="px-6 py-4 font-medium text-light-text dark:text-dark-text dark:text-white">{t.name}</td>
                                                <td className="px-6 py-4 text-sm text-light-subtle dark:text-dark-subtle">{t.unitName}</td>
                                                <td className="px-6 py-4 text-sm text-light-subtle dark:text-dark-subtle">
                                                    <div>{t.email}</div>
                                                    <div className="text-xs">{t.phone}</div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium 
                                                        ${t.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 dark:bg-gray-100 dark:bg-gray-800 text-gray-700'}`}>
                                                        {t.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === 'financials' && (
                    <div className="bg-light-card dark:bg-dark-card dark:bg-dark-card rounded-lg shadow-sm border border-light-border dark:border-dark-border overflow-hidden">
                        <div className="p-6 border-b border-light-border dark:border-dark-border flex justify-between items-center">
                            <h3 className="text-lg font-bold text-light-text dark:text-dark-text dark:text-white">Recent Transactions</h3>
                            <button
                                onClick={() => navigate('/landlord/financials')}
                                className="text-sm text-primary hover:underline"
                            >
                                View All Financials
                            </button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-light-bg dark:bg-dark-bg dark:bg-gray-900/50">
                                    <tr>
                                        <th className="px-6 py-4 text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Tenant / Unit</th>
                                        <th className="px-6 py-4 text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Amount</th>
                                        <th className="px-6 py-4 text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Balance</th>
                                        <th className="px-6 py-4 text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-4 text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Due Date</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-light-border dark:divide-dark-border dark:divide-light-border dark:divide-dark-border">
                                    {propertyInvoices.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="px-6 py-8 text-center text-light-subtle dark:text-dark-subtle">
                                                No financial records found for this property.
                                            </td>
                                        </tr>
                                    ) : (
                                        propertyInvoices.map((inv) => (
                                            <tr key={inv.id} className="hover:bg-light-bg dark:bg-dark-bg dark:hover:bg-gray-900/50">
                                                <td className="px-6 py-4">
                                                    <div className="font-medium text-light-text dark:text-dark-text dark:text-white">{inv.tenant_name}</div>
                                                    <div className="text-xs text-light-subtle dark:text-dark-subtle">{inv.unit_number}</div>
                                                </td>
                                                <td className="px-6 py-4 font-medium">KES {inv.amount.toLocaleString()}</td>
                                                <td className="px-6 py-4 text-sm text-danger dark:text-danger-dark">
                                                    {inv.balance > 0 ? `KES ${inv.balance.toLocaleString()}` : '-'}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium 
                                                        ${inv.status === 'Paid' ? 'bg-green-100 text-green-700' :
                                                            inv.status === 'Overdue' ? 'bg-red-100 text-red-700' :
                                                                'bg-yellow-100 text-yellow-700'}`}>
                                                        {inv.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-light-subtle dark:text-dark-subtle">
                                                    {new Date(inv.due_date).toLocaleDateString()}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
};
